import { Injectable } from '@angular/core';
import { createSelector, select, Store } from '@ngrx/store';
import { Map } from 'immutable';
import { Observable } from 'rxjs';

import { SimulationResultEntity } from '../components/subscription-simulator/simulation-result.entity';
import { ColumnConfigItemEntity } from '../domain/column-config-item.entity';
import { ColumnModelItemEntity } from '../domain/column-model-item.entity';
import { FilterModelItemEntity } from '../domain/filter-model-item.entity';
import { ProfileItemEntity } from '../domain/profile-item.entity';
import { PublicMetadataEntity } from '../domain/public-metadata.entity';
import { SubscriptionStatus } from '../domain/subscription.types';
import { featureSelector, SubscriptionRootState } from './feature.store';
import {
	SubscriptionSimulatorSliceKey,
	SubscriptionSimulatorSliceState,
} from './subscription-simulator.state';

const baseSelector = createSelector(
	featureSelector,
	(state) => state[SubscriptionSimulatorSliceKey]
);

const PreSelectedProfileForSimulation = createSelector(baseSelector, (state) =>
	state.get('preSelectedProfile')
);

const PreSelectedColumnModelForSimulation = createSelector(
	baseSelector,
	(state) => state.get('preSelectedColumnModel')
);

const SimulationSubscriptionId = createSelector(baseSelector, (state) =>
	state.get('simulationSubscriptionId')
);

const SubscriptionCurrentStatus = createSelector(baseSelector, (state) =>
	state.get('subscriptionStatus')
);

const SimulationMetadata = createSelector(baseSelector, (state) =>
	state.get('simulationMetadata')
);

const SubscriptionColumns = createSelector(baseSelector, (state) =>
	state.get('subscriptionColumns')
);

const ProfilesForSimulation = createSelector(baseSelector, (state) =>
	state.get('profilesForSimulation')
);

const ColumnModelsForSimulation = createSelector(baseSelector, (state) =>
	state.get('columnModelsForSimulation')
);

const FilterModelsForSimulation = createSelector(baseSelector, (state) =>
	state.get('filterModelsForSimulation')
);

const IsSimulationLoading = createSelector(baseSelector, (state) =>
	state.get('loading')
);

const simulationData = createSelector(baseSelector, (state) =>
	state.get('simulationData')
);

@Injectable()
export class SubscriptionSimulatorSelector {
	public constructor(private readonly store: Store<SubscriptionRootState>) {}

	private get state(): Observable<SubscriptionSimulatorSliceState> {
		return this.store.pipe(select(baseSelector));
	}

	public isSimulationLoading$(): Observable<boolean> {
		return this.store.pipe(select(IsSimulationLoading));
	}

	public getPreSelectedProfileForSimulation$(): Observable<string> {
		return this.store.pipe(select(PreSelectedProfileForSimulation));
	}

	public getPreSelectedColumnModelForSimulation$(): Observable<string> {
		return this.store.pipe(select(PreSelectedColumnModelForSimulation));
	}

	public getSimulationSubscriptionId$(): Observable<number> {
		return this.store.pipe(select(SimulationSubscriptionId));
	}

	public getSubscriptionStatus$(): Observable<SubscriptionStatus> {
		return this.store.pipe(select(SubscriptionCurrentStatus));
	}

	public getSimulationMetadata$(): Observable<PublicMetadataEntity> {
		return this.store.pipe(select(SimulationMetadata));
	}

	public getSubscriptionColumns$(): Observable<
		Map<string, ColumnConfigItemEntity>
	> {
		return this.store.pipe(select(SubscriptionColumns));
	}

	public getProfilesForSimulation$(): Observable<
		Map<string, ProfileItemEntity>
	> {
		return this.store.pipe(select(ProfilesForSimulation));
	}

	public getColumnModelsForSimulation$(): Observable<
		Map<string, ColumnModelItemEntity>
	> {
		return this.store.pipe(select(ColumnModelsForSimulation));
	}

	public getFilterModelsForSimulation$(): Observable<
		Map<string, FilterModelItemEntity>
	> {
		return this.store.pipe(select(FilterModelsForSimulation));
	}

	public getSimulationResult$(): Observable<SimulationResultEntity | null> {
		return this.store.pipe(select(simulationData));
	}
}
